import { useState } from "react";
import { useEffect } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ChevronDown, BookOpen, Upload, CreditCard, TrendingUp, UserCog, LifeBuoy } from "lucide-react";

export const FAQS = [
  { q: "How much does it cost to publish my book?", a: "We offer three plans — Essential, Professional and Premium. You'll see current prices on the plan selection step of your submission and on the home page. Payment is a one-time fee per book, charged securely via Stripe when you submit." },
  { q: "What file formats can I upload?", a: "Manuscripts: .docx, .pdf or .epub. Covers: .jpg, .png, .pdf or .webp. If you don't have a cover, choose 'Brief our designers' during submission and describe what you want." },
  { q: "Can I change my manuscript after submitting?", a: "After payment your submission is locked so production can begin. Upload a new version before paying, or message our team from your project page — we can upload revised files for you during the production stages." },
  { q: "How do royalties work?", a: "Amazon pays royalties on each sale. Your royalty split (shown on your project page, e.g. 70% to you) is applied to every imported sale. Your Sales & Royalties dashboard shows gross royalty, your share, and what's been paid out to you." },
  { q: "When do I get paid?", a: "Our team disburses outstanding royalty balances periodically. Every payout is recorded in your Sales & Royalties page and you'll receive an email notification. Make sure your payout details are up to date in your Profile." },
  { q: "How current is my sales data?", a: "Sales come from official Amazon sales reports that our team imports regularly (typically monthly, matching Amazon's royalty cycle). Your dashboard updates as soon as a new report is imported." },
  { q: "What are the production stages?", a: "Submitted → Under Review → Manuscript Prep → Design → Formatting → Ready to Publish → Publishing → Live. Your project page shows a progress bar, full status history and any messages from our team. You get an email at every stage change." },
  { q: "I got an invite link — what is it?", a: "If we published for you before you had an account, we created one from our records. The claim link lets you set your own email and password. After claiming, you'll see your books, sales and payout history." },
  { q: "I forgot my password.", a: "Use 'Forgot password?' on the login page. You'll receive a reset token — enter it with your new password on the same page." },
];

const HOWTOS = [
  {
    icon: BookOpen, title: "Navigating the platform",
    steps: [
      "My Books — your home page. Every book you've submitted appears as a card with its current production stage and progress bar.",
      "New Submission — start a new book. Your progress saves as a draft at every step, so you can leave and come back.",
      "Sales & Royalties — charts of units sold and earnings, breakdowns by format and marketplace, payout history, and your downloadable statement.",
      "Profile — your personal details, pen names, tax info, payout details, and payment receipts.",
      "Click any book card to open its project page: status history, files, and a message thread with our team.",
    ],
  },
  {
    icon: Upload, title: "Submitting a manuscript",
    steps: [
      "Click 'New Submission' and fill in your book details — title, description, genre, keywords and target price. Click 'Save & continue'.",
      "Step 2: upload your manuscript (.docx, .pdf or .epub). Uploading again later creates a new version — nothing is overwritten.",
      "Step 3: upload your cover (multiple sizes welcome), or switch to 'Brief our designers' and describe the cover you want.",
      "Step 4: choose a plan and click 'Pay & submit'. You'll be taken to a secure Stripe checkout.",
      "After payment your book enters the production pipeline and is locked for changes. You'll get a receipt by email and can follow every stage from My Books.",
    ],
  },
  {
    icon: UserCog, title: "Updating your details",
    steps: [
      "Profile → edit your display name, legal name, pen names, bio, tax info and payout details, then click 'Save profile'.",
      "Keep payout details current — royalty disbursements are sent using this information.",
      "A draft book's details can be edited any time: open the book and click 'Resume submission'.",
      "After submission, request changes (title, description, price, files) by posting a message on the project page — our team will handle it.",
    ],
  },
  {
    icon: CreditCard, title: "Payments & receipts",
    steps: [
      "Service fees are paid once per book, at submission, through Stripe checkout.",
      "Every payment appears under Profile → Payments & Receipts with its status.",
      "If a checkout is interrupted, just reopen the book and pay again — you're only charged when Stripe confirms success.",
    ],
  },
  {
    icon: TrendingUp, title: "Tracking sales & getting paid",
    steps: [
      "Open Sales & Royalties to see lifetime units, your earnings, amounts paid out, and your outstanding balance.",
      "Charts show earnings over time and units by month; tables break results down by format, marketplace and book.",
      "Click 'Download statement' for a CSV of every sale with your share calculated.",
      "Payouts appear in the 'Payout history' table and you're emailed each time one is disbursed.",
    ],
  },
];

export default function Help() {
  const [tab, setTab] = useState("howto");
  const [open, setOpen] = useState(null);
  const [faqs, setFaqs] = useState(FAQS);
  useEffect(() => { api.get("/site-content").then((r) => { if (r.data.faqs?.length) setFaqs(r.data.faqs); }).catch(() => {}); }, []);

  return (
    <div className="max-w-4xl" data-testid="help-page">
      <div className="mb-8">
        <h1 className="text-3xl font-bold tracking-tight text-neutral-900 font-heading flex items-center gap-3"><LifeBuoy size={28} /> Help Center</h1>
        <p className="text-neutral-500 mt-1">Everything you need to publish with GloriPub.</p>
      </div>

      <div className="flex gap-2 mb-8">
        <button data-testid="help-tab-howto" onClick={() => setTab("howto")}
          className={`px-4 py-1.5 rounded-md text-sm font-medium border ${tab === "howto" ? "bg-neutral-900 text-white border-neutral-900" : "border-neutral-300 text-neutral-600 hover:bg-neutral-50"}`}>
          How-To Guides
        </button>
        <button data-testid="help-tab-faq" onClick={() => setTab("faq")}
          className={`px-4 py-1.5 rounded-md text-sm font-medium border ${tab === "faq" ? "bg-neutral-900 text-white border-neutral-900" : "border-neutral-300 text-neutral-600 hover:bg-neutral-50"}`}>
          FAQ
        </button>
      </div>

      {tab === "howto" && (
        <div className="space-y-6">
          {HOWTOS.map((h) => (
            <div key={h.title} className="bg-white border border-neutral-200 rounded-lg p-8" data-testid={`howto-${h.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <h3 className="font-semibold font-heading text-lg text-neutral-900 flex items-center gap-2.5 mb-4">
                <h.icon size={19} className="text-neutral-500" /> {h.title}
              </h3>
              <ol className="space-y-3">
                {h.steps.map((s, i) => (
                  <li key={i} className="flex gap-3 text-sm text-neutral-700 leading-relaxed">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-neutral-100 text-neutral-600 text-xs font-semibold flex items-center justify-center mt-0.5">{i + 1}</span>
                    {s}
                  </li>
                ))}
              </ol>
            </div>
          ))}
        </div>
      )}

      {tab === "faq" && (
        <div className="bg-white border border-neutral-200 rounded-lg divide-y divide-neutral-100">
          {faqs.map((f, i) => (
            <div key={i}>
              <button data-testid={`faq-question-${i}`} onClick={() => setOpen(open === i ? null : i)}
                className="w-full flex items-center justify-between gap-4 text-left px-6 py-4 hover:bg-neutral-50 transition-colors">
                <span className="font-medium text-neutral-900 text-sm">{f.q}</span>
                <ChevronDown size={16} className={`flex-shrink-0 text-neutral-400 transition-transform ${open === i ? "rotate-180" : ""}`} />
              </button>
              {open === i && <p data-testid={`faq-answer-${i}`} className="px-6 pb-5 text-sm text-neutral-600 leading-relaxed">{f.a}</p>}
            </div>
          ))}
        </div>
      )}

      <div className="bg-neutral-100 border border-neutral-200 rounded-lg p-6 mt-8 text-sm text-neutral-600">
        Still stuck? Post a message on any of your project pages — our team replies there and you'll be notified by email.
      </div>
    </div>
  );
}
